import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { MeResponse, ProductCard, ProductLastPurchase, ProductListItem, StockWarehouseRef } from '@kassa/shared';
import { api, ApiError } from '../../api';
import { CameraScan, cameraScanSupported } from '../../components/CameraScan';
import { formatDate, formatMoney, formatQty, hasPermission, UNIT_LABEL } from '../../format';
import { SALE_PENDING_KEY } from '../../salePending';
import { CountMode } from './CountMode';
import { flushQueue, indexedDbBackend, memoryBackend, type QueueBackend, type QueueItem, type SendResult } from '../../offlineQueue';

// Mobil skaner (KT-108, /skaner): telefonda kamera, əl ilə və ya USB/Bluetooth skaner (klaviatura kimi).
// Rejimlər: «Qiymət, qalıq» (mal kartı, anbarlar üzrə qalıq, alış qiyməti yalnız cost.view ilə) və «Malı aldım» (qəbul — internetsiz
// növbə ilə, UUID ilə bir dəfə yazılır). «Sayım» — sayım sənədi API-si hazır olanda (KT-108 (6b)).

type Mode = 'price' | 'count' | 'receive';

interface Found {
  card: ProductCard;
  /** null — alış yoxdur və ya icazə yoxdur; 'failed' — yüklənmədi (səhv «alış yoxdur» görünməsin) */
  last: ProductLastPurchase | null | 'failed';
}
interface RecvLine {
  productId: string;
  name: string;
  unit: string;
  qty: number;
  /** Yazılan mətn (vergüllə «1,5» — yazarkən rəqəmə çevrilmir) */
  text: string;
}

/** İnternet yoxdursa fetch TypeError atır — insan dilində. */
const errText = (e: unknown) =>
  e instanceof ApiError ? e.message : e instanceof TypeError ? 'İnternet yoxdur — mal axtarışı mümkün deyil' : e instanceof Error ? e.message : 'Xəta baş verdi';
const qtyText = (n: number) => String(n).replace('.', ',');
const q3 = (n: number) => Number(n.toFixed(3)); // miqdar 3 onluq (float qalığı olmasın)

/**
 * Kodla mal. Dəqiq barkod üstündür. exact = true (qəbul): yalnız dəqiq uyğunluq — ad axtarışının ilk nəticəsi
 * səhv malı qalığa yazmasın.
 */
async function findByCode(code: string, exact: boolean): Promise<ProductListItem | null> {
  const r = await api.products({ q: code, limit: 5 });
  const hit = r.items.find((p) => p.barcodes.includes(code));
  if (hit || exact) return hit ?? (r.items.length === 1 && /^\d{4,}$/.test(code) ? r.items[0] : null);
  return r.items[0] ?? null;
}

export function Scanner({ me, onExit, onSale }: { me: MeResponse; onExit: () => void; onSale: () => void }) {
  const [mode, setMode] = useState<Mode>('price');
  const [code, setCode] = useState('');
  const [camera, setCamera] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; bad: boolean } | null>(null);
  const [found, setFound] = useState<Found | null>(null);
  const [recent, setRecent] = useState<{ id: string; name: string; price: number | null }[]>([]);
  const [recv, setRecv] = useState<RecvLine[]>([]);
  const [warehouses, setWarehouses] = useState<StockWarehouseRef[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [queue, setQueue] = useState<{ backend: QueueBackend; items: QueueItem[] } | null>(null);
  const [authLost, setAuthLost] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  // Sürətli skaner: oxunuşlar növbə ilə işlənir, köhnə cavab yeni kartın üstünə yazılmır
  const chain = useRef<Promise<void>>(Promise.resolve());
  // «Sayım» rejimi açıq sayımda oxunan malı qəbul edir
  const countAdd = useRef<((p: ProductListItem) => void) | null>(null);
  const seq = useRef(0);
  const canCost = hasPermission(me, 'cost.view');
  const canPrice = hasPermission(me, 'price.edit');
  const canReceive = hasPermission(me, 'stock.receive');
  const canCount = hasPermission(me, 'stock.count');
  const canSell = hasPermission(me, 'sale.create');

  // Növbə (IndexedDB; yoxdursa yaddaş) və anbarlar
  useEffect(() => {
    indexedDbBackend()
      .then(async (b) => {
        const backend = b ?? memoryBackend();
        setQueue({ backend, items: await backend.all() });
      })
      .catch((e: unknown) => {
        console.error('kassa.scanner: növbə açılmadı', e instanceof Error ? e.message : e);
        setQueue({ backend: memoryBackend(), items: [] });
      });
    if (canReceive || canCount) {
      api
        .stockWarehouses()
        .then((w) => {
          setWarehouses(w);
          setWarehouseId((cur) => cur || w[0]?.id || '');
        })
        .catch((e: unknown) => setMsg({ text: `Anbarlar yüklənmədi: ${errText(e)}`, bad: true }));
    }
  }, []);

  // Növbə istifadəçiyə bağlıdır: telefonda başqası girsə, əvvəlkinin qəbulu onun adından getməsin
  const ownerKey = `${me.tenant?.tenantId ?? ''}/${me.tenant?.membershipId ?? ''}`;
  const send = async (i: QueueItem): Promise<SendResult> => {
    if ((i.meta as { owner?: string } | undefined)?.owner !== ownerKey) return { kind: 'skip' };
    // Yalnız tanınan ünvanlar (cihaz yaddaşından gələn path-ə kor-koranə POST edilmir)
    if (i.path !== '/api/intake/free' && !/^\/api\/counts\/[0-9a-f-]{36}\/scans$/.test(i.path)) return { kind: 'rejected', message: 'naməlum element' };
    try {
      await api.queuePost(i.path, i.body);
      return { kind: 'ok' };
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 401) return { kind: 'auth' };
        // 4xx — server rədd etdi (istifadəçi silir və ya geri yükləyib düzəldir); 408/429/5xx — sonra yenidən
        if (e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429) return { kind: 'rejected', message: e.message };
        return { kind: 'network' };
      }
      if (!(e instanceof TypeError)) console.error('kassa.scanner: göndəriş xətası', e instanceof Error ? e.message : e);
      return { kind: 'network' };
    }
  };
  const flush = async (q = queue) => {
    if (!q) return;
    try {
      const r = await flushQueue(q.backend, send);
      setAuthLost(r.stopped === 'auth');
      if (r.sent) setMsg({ text: `Göndərildi: ${r.sent}`, bad: false });
      if (r.storeErrors) setMsg({ text: 'Cihaz yaddaşına yazılmadı — növbə yenidən yoxlanacaq', bad: true });
    } catch (e) {
      console.error('kassa.scanner: növbə göndərilmədi', e instanceof Error ? e.message : e);
      setMsg({ text: 'Növbə oxunmadı — səhifəni yeniləyin', bad: true });
    }
    setQueue({ backend: q.backend, items: await q.backend.all().catch(() => []) });
  };
  useEffect(() => {
    if (!queue) return;
    void flush(queue);
    const on = () => void flush(queue);
    window.addEventListener('online', on);
    // İnternet var, amma server cavab vermədi (5xx) — 30 saniyədən bir yenidən
    const t = window.setInterval(() => {
      if (navigator.onLine) void flush(queue);
    }, 30_000);
    return () => {
      window.removeEventListener('online', on);
      window.clearInterval(t);
    };
  }, [queue?.backend]);
  // Yaddaş rejimində göndərilməmiş qəbul var — səhifə bağlanarkən xəbərdarlıq
  const unsafe = queue !== null && !queue.backend.durable && queue.items.some((i) => !i.error);
  useEffect(() => {
    if (!unsafe) return;
    const h = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [unsafe]);
  const leave = (go: () => void) => {
    if (unsafe && !window.confirm('Göndərilməmiş qəbul var və bu brauzer onu saxlaya bilmir. Çıxsanız itəcək. Çıxılsın?')) return;
    go();
  };

  const lookup = (raw: string) => {
    const c = raw.trim();
    if (c.length < 2) return;
    setCode(''); // növbəti skan bu mətnin üstünə yazılmasın
    const my = ++seq.current;
    const m = mode;
    chain.current = chain.current.then(() => lookupOne(c, m, my));
  };
  const lookupOne = async (c: string, m: Mode, my: number) => {
    setBusy(true);
    setMsg(null);
    try {
      const p = await findByCode(c, m !== 'price');
      if (!p) {
        setMsg({ text: m !== 'price' ? `«${c}» dəqiq kodla tapılmadı — «Qiymət, qalıq»-da adla axtarın` : `«${c}» tapılmadı`, bad: true });
        return;
      }
      setRecent((r) => [{ id: p.id, name: p.name, price: p.retailPrice }, ...r.filter((x) => x.id !== p.id)].slice(0, 10));
      if (m === 'count') {
        if (countAdd.current) countAdd.current(p);
        else setMsg({ text: 'Əvvəl sayımı açın və ya yenisini başladın', bad: true });
        return;
      }
      if (m === 'receive') {
        const add = p.matchedQty ?? 1;
        setRecv((cur) => {
          const i = cur.findIndex((l) => l.productId === p.id);
          if (i >= 0) return cur.map((l, k) => (k === i ? { ...l, qty: q3(l.qty + add), text: qtyText(q3(l.qty + add)) } : l));
          return [...cur, { productId: p.id, name: p.name, unit: p.unitCode, qty: add, text: qtyText(add) }];
        });
        return;
      }
      const [card, last] = await Promise.all([
        api.productCard(p.id),
        canCost
          ? api.productLastPurchase(p.id).catch((e: unknown) => {
              console.error('kassa.scanner: son alış alınmadı', e instanceof Error ? e.message : e);
              return 'failed' as const;
            })
          : Promise.resolve(null),
      ]);
      if (my === seq.current) setFound({ card, last });
    } catch (e) {
      setMsg({ text: errText(e), bad: true });
    } finally {
      setBusy(false);
      input.current?.focus();
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    lookup(code);
  };

  const toSale = () => {
    if (!found) return;
    try {
      window.sessionStorage.setItem(SALE_PENDING_KEY, JSON.stringify({ productId: found.card.id }));
    } catch {
      setMsg({ text: 'Brauzer yaddaşı əlçatmazdır — malı satış ekranında axtarın', bad: true });
      return;
    }
    leave(onSale);
  };

  const changePrice = async () => {
    if (!found) return;
    const v = window.prompt(`«${found.card.name}» — yeni pərakəndə qiymət, ₼`, found.card.retailPrice === null ? '' : String(found.card.retailPrice).replace('.', ','));
    if (v === null) return;
    const n = Number(v.replace(',', '.').trim());
    if (!Number.isFinite(n) || n < 0 || !/^\d+([.,]\d{1,4})?$/.test(v.trim())) {
      setMsg({ text: 'Qiymət düzgün deyil', bad: true });
      return;
    }
    setBusy(true);
    try {
      await api.productSetPrice(found.card.id, n);
      setFound({ ...found, card: await api.productCard(found.card.id) });
      setMsg({ text: 'Qiymət dəyişdi', bad: false });
    } catch (e) {
      setMsg({ text: errText(e), bad: true });
    } finally {
      setBusy(false);
    }
  };

  const sendReceive = async () => {
    if (!queue) {
      setMsg({ text: 'Cihaz növbəsi hələ açılmayıb — bir az gözləyin', bad: true });
      return;
    }
    if (recv.length === 0 || !warehouseId) return;
    const id = crypto.randomUUID();
    const item: QueueItem = {
      id,
      path: '/api/intake/free',
      body: { id, warehouseId, lines: recv.map((l) => ({ productId: l.productId, qty: l.qty })) },
      createdAt: new Date().toISOString(),
      error: null,
      label: `Qəbul: ${recv.length} mal`,
      meta: { lines: recv, warehouseId, owner: ownerKey },
    };
    try {
      await queue.backend.put(item); // əvvəl cihazda, sonra göndəriş — internet kəsilsə də itmir
    } catch (e) {
      console.error('kassa.scanner: qəbul cihaza yazılmadı', e instanceof Error ? e.message : e);
      setMsg({ text: 'Qəbul cihaza yazılmadı — sətirlər qalır, yenidən cəhd edin', bad: true });
      return;
    }
    setRecv([]);
    setMsg({ text: 'Qəbul növbəyə yazıldı', bad: false });
    await flush(queue);
  };
  /** Növbəyə element (əvvəl cihaza, sonra göndəriş). false — cihaza yazılmadı (istifadəçiyə deyilib). */
  const enqueue = async (item: QueueItem): Promise<boolean> => {
    if (!queue) {
      setMsg({ text: 'Cihaz növbəsi hələ açılmayıb — bir az gözləyin', bad: true });
      return false;
    }
    try {
      await queue.backend.put(item);
    } catch (e) {
      console.error('kassa.scanner: oxunuş cihaza yazılmadı', e instanceof Error ? e.message : e);
      setMsg({ text: 'Oxunuş cihaza yazılmadı — yenidən oxudun', bad: true });
      return false;
    }
    setQueue({ backend: queue.backend, items: await queue.backend.all().catch(() => [...queue.items, item]) });
    void flush(queue);
    return true;
  };
  /** Rədd olunan qəbul: silmək və ya sətirləri ekrana qaytarıb düzəltmək */
  const dropItem = async (i: QueueItem, restore: boolean) => {
    if (!queue) return;
    const meta = i.meta as { lines?: RecvLine[]; warehouseId?: string } | undefined;
    if (restore && meta?.lines) {
      setRecv(meta.lines.map((l) => ({ ...l, text: qtyText(l.qty) })));
      if (meta.warehouseId) setWarehouseId(meta.warehouseId);
      setMode('receive');
    }
    try {
      await queue.backend.del(i.id);
    } catch (e) {
      setMsg({ text: `Silinmədi: ${errText(e)}`, bad: true });
    }
    setQueue({ backend: queue.backend, items: await queue.backend.all().catch(() => []) });
  };

  const isMine = (i: QueueItem) => (i.meta as { owner?: string } | undefined)?.owner === ownerKey;
  const pending = queue?.items.filter((i) => !i.error && isMine(i)) ?? [];
  const others = queue?.items.filter((i) => !isMine(i)).length ?? 0;
  const rejected = queue?.items.filter((i) => i.error && isMine(i)) ?? [];

  return (
    <div className="scanner">
      <header className="scanner-head">
        <div>
          <strong>Skaner</strong>
          <span className="muted small">
            {me.outlet?.name ?? ''} · {me.accountName}
          </span>
        </div>
        <button type="button" className="btn-small scanner-exit" onClick={() => leave(onExit)}>
          Bağla
        </button>
      </header>

      <div className="scanner-modes" role="group" aria-label="Rejim">
        {(
          [
            ['price', 'Qiymət, qalıq'],
            ['count', 'Sayım'],
            ['receive', 'Malı aldım'],
          ] as [Mode, string][]
        ).map(([k, l]) => (
          <button key={k} type="button" aria-pressed={mode === k} className={mode === k ? 'on' : ''} onClick={() => setMode(k)} disabled={(k === 'receive' && !canReceive) || (k === 'count' && !canCount)}>
            {l}
          </button>
        ))}
      </div>

      {(pending.length > 0 || (queue && !queue.backend.durable)) && (
        <div className="chip-warn scanner-queue" role="status">
          <span>
            {pending.length > 0 && (authLost ? `Sessiya bitib — yenidən daxil olun · ${pending.length} növbədə. ` : `Göndərilməyib · ${pending.length} növbədə. `)}
            {queue && !queue.backend.durable && 'Bu brauzerdə cihaz yaddaşı yoxdur — səhifəni bağlamayın.'}
          </span>
          {pending.length > 0 && (
            <button type="button" className="btn-small" onClick={() => void flush()}>
              Göndər
            </button>
          )}
        </div>
      )}
      {others > 0 && <p className="muted small">Bu telefonda başqa istifadəçinin {others} göndərilməmiş qəbulu var — o daxil olanda göndəriləcək.</p>}
      {rejected.map((r) => (
        <div key={r.id} className="bad scanner-queue" role="alert">
          <span>
            {r.label} rədd olundu: {r.error}
          </span>
          <span className="scanner-col-actions">
            {Array.isArray((r.meta as { lines?: unknown } | undefined)?.lines) && (
              <button type="button" className="btn-small" onClick={() => void dropItem(r, true)}>
                Geri yüklə
              </button>
            )}
            <button type="button" className="btn-small" onClick={() => void dropItem(r, false)}>
              Sil
            </button>
          </span>
        </div>
      ))}

      <form className="scanner-input" onSubmit={submit}>
        <input
          ref={input}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Barkod, kod və ya ad"
          aria-label="Barkod, kod və ya ad"
          inputMode="search"
          autoFocus
        />
        <button type="submit" className="btn-primary" disabled={busy || code.trim().length < 2}>
          Tap
        </button>
      </form>
      {cameraScanSupported() && (
        <button type="button" className="btn-secondary scanner-camera" onClick={() => setCamera(true)}>
          Kamera ilə oxu
        </button>
      )}
      <p className="muted small">USB və Bluetooth skaner da işləyir — klaviatura kimi, quraşdırmasız.</p>
      {camera && (
        <CameraScan
          onClose={() => setCamera(false)}
          onCode={(c) => {
            setCamera(false);
            lookup(c);
          }}
        />
      )}

      {msg && (
        <p className={msg.bad ? 'bad' : 'ok'} role={msg.bad ? 'alert' : 'status'}>
          {msg.text}
        </p>
      )}

      {mode === 'price' && found && (
        <section className="card scanner-card" aria-label="Oxunan mal">
          <p className="muted small">
            {found.card.categoryName ?? 'Kateqoriyasız'} · <span className={`badge ${found.card.active ? 'badge-ok' : 'badge-off'}`}>{found.card.active ? 'Aktiv' : 'Passiv'}</span>
          </p>
          <h2>{found.card.name}</h2>
          <p className="muted small mono">{found.card.barcodes.map((b) => b.barcode).join(' · ') || '—'}</p>
          <ul className="scanner-prices">
            {found.card.prices.map((p) => (
              <li key={p.priceTypeCode}>
                <span>{p.priceTypeName}</span>
                <strong className="mono">{formatMoney(p.price)}</strong>
              </li>
            ))}
            {found.card.prices.length === 0 && <li className="muted">Qiymət yoxdur</li>}
          </ul>
          <h3>
            Qalıq — cəmi {formatQty(found.card.qtyPhys)} {UNIT_LABEL[found.card.unitCode] ?? found.card.unitCode}
          </h3>
          <ul className="scanner-stock">
            {found.card.stock.map((s) => (
              <li key={s.warehouseId}>
                <span>{s.warehouseName}</span>
                <span className="mono">
                  {formatQty(s.qtyPhys)}
                  {s.qtyReserved > 0 ? ` (rezerv ${formatQty(s.qtyReserved)})` : ''}
                </span>
              </li>
            ))}
            {found.card.stock.length === 0 && <li className="muted">Anbarda yoxdur</li>}
          </ul>
          {canCost && (
            <p className="small">
              {found.last === 'failed'
                ? 'Son alış yüklənmədi'
                : found.last
                ? `Son alış ${found.last.unitPrice === null ? '—' : formatMoney(Number(found.last.unitPrice))} · ${formatDate(found.last.date)} · ${found.last.supplierName ?? 'təchizatçı yoxdur'}`
                : 'Alış yoxdur'}
            </p>
          )}
          <div className="scanner-actions">
            {canSell && (
              <button type="button" className="btn-primary" onClick={toSale}>
                Satışa əlavə et
              </button>
            )}
            {canPrice && (
              <button type="button" className="btn-secondary" disabled={busy} onClick={() => void changePrice()}>
                Qiyməti dəyiş
              </button>
            )}
          </div>
        </section>
      )}

      {mode === 'count' && (
        <CountMode
          me={me}
          owner={ownerKey}
          items={queue?.items ?? []}
          warehouses={warehouses}
          enqueue={enqueue}
          flush={() => flush()}
          register={(fn) => {
            countAdd.current = fn;
          }}
          setMsg={setMsg}
        />
      )}

      {mode === 'receive' && (
        <section className="card scanner-card" aria-label="Malı aldım">
          <label className="field">
            <span>Anbar</span>
            <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </label>
          {recv.length === 0 && <p className="muted">Malı oxudun — sətir əlavə olunur, təkrar oxudanda say artır.</p>}
          <ul className="scanner-lines">
            {recv.map((l, i) => (
              <li key={l.productId}>
                <span className="scanner-line-name">{l.name}</span>
                <span className="scanner-stepper">
                  <button type="button" aria-label={`${l.name} azalt`} onClick={() => setRecv((cur) => cur.map((x, k) => (k === i ? { ...x, qty: q3(Math.max(0, x.qty - 1)), text: qtyText(q3(Math.max(0, x.qty - 1))) } : x)).filter((x) => x.qty > 0))}>
                    −
                  </button>
                  <input
                    aria-label={`${l.name} miqdar`}
                    inputMode="decimal"
                    value={l.text}
                    onChange={(e) => {
                      // Mətn saxlanır («1,» yazılarkən itmir); say yalnız düzgün rəqəmdə yenilənir (3 onluq)
                      const t = e.target.value;
                      const ok = /^\d{0,6}([.,]\d{0,3})?$/.test(t);
                      if (!ok) return;
                      const n = Number(t.replace(',', '.'));
                      setRecv((cur) => cur.map((x, k) => (k === i ? { ...x, text: t, qty: t === '' || !Number.isFinite(n) ? 0 : q3(n) } : x)));
                    }}
                  />
                  <button type="button" aria-label={`${l.name} artır`} onClick={() => setRecv((cur) => cur.map((x, k) => (k === i ? { ...x, qty: q3(x.qty + 1), text: qtyText(q3(x.qty + 1)) } : x)))}>
                    +
                  </button>
                </span>
                <span className="muted small">{UNIT_LABEL[l.unit] ?? l.unit}</span>
              </li>
            ))}
          </ul>
          <button type="button" className="btn-primary scanner-wide" disabled={recv.length === 0 || !warehouseId || recv.some((l) => l.qty <= 0)} onClick={() => void sendReceive()}>
            Qəbulu yaz ({recv.length})
          </button>
          <p className="muted small">İnternet yoxdursa qəbul cihazda saxlanır və internet gələndə özü göndərilir (bir dəfə yazılır).</p>
        </section>
      )}

      {mode === 'price' && recent.length > 0 && (
        <section aria-label="Son oxunanlar" className="scanner-recent">
          <h3 className="muted small">SON OXUNANLAR</h3>
          <ul>
            {recent.map((r) => (
              <li key={r.id}>
                <span>{r.name}</span>
                <span className="mono">{r.price === null ? '—' : formatMoney(r.price)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
